import { BaseApiService } from "../base/base-api.service";
import { SaveResponse } from "../../models/api/save";
import { Page } from "../../models/api/page/page";
import { TransactionDTO, TransactionSTO } from "../../models/transactions";
import { TransactionFilter } from "../../models/transactions/transaction.filter";
import { SearchOptions } from "../../models/search-options/search-options";

import { TransactionStatusQuickFilter } from "../../containers/Quickfilters/TransactionStatusQuickfilter/interfaces";
import { PeriodQuickFilter } from "../../containers/Quickfilters/PeriodQuickfilter/interfaces";
import { ClarificationQuickFilter } from "../../containers/Quickfilters/ClarificationQuickfilter/interfaces";
import { LegalEntityQuickFilter } from "../../containers/Quickfilters/LegalEntityQuickfilter/interfaces";

import { toastifyResponse } from "../../utils/toastify-response/toastify-response";
import { ListSortDirection, Sort } from "../../models/api/sort";

export class TransactionsApiService extends BaseApiService<TransactionDTO, TransactionSTO> {
  constructor() {
    super({
      servicePath: "/Transactions",
      loadItemsPath: "/GetTransactions",
      saveItemsPath: "/SaveTransactions",
    });
    this.defaultSort = [
      {
        name: "createdAt",
        direction: ListSortDirection.Descending,
      } as Sort,
    ];
  }
}

export const loadTransactions = async (
  propsFilter: {
    transactionStatuses: TransactionStatusQuickFilter["transactionStatuses"];
    period: PeriodQuickFilter["period"];
    clarifications: ClarificationQuickFilter["clarifications"];
    legalEntities: LegalEntityQuickFilter["legalEntities"];
    clients: string[];
    vendors: string[];
    types: string[];
  },
  page: Page,
  searchOptions: SearchOptions,
  signal: AbortSignal
) => {
  const { query, exactMatch } = searchOptions;

  const response = await new TransactionsApiService().loadItems(
    {
      ids: [],
      isDeleted: false,
      withoutDocuments: false,
      documentStatuses: [],
      createdByUsers: [],
      modifiedByUsers: [],
      statuses: propsFilter.transactionStatuses.statuses,
      period: propsFilter.period.period,
      withoutPeriod: propsFilter.period.withoutPeriod,
      ...propsFilter.clarifications,
      ...propsFilter.legalEntities,
      clients: propsFilter.clients,
      vendors: propsFilter.vendors,
      types: propsFilter.types,
    } as TransactionFilter,
    query,
    undefined,
    page,
    exactMatch,
    60,
    signal
  );

  toastifyResponse(response, "failed");

  return response;
};

export const loadTransaction = async (id?: string) => {
  const response = await new TransactionsApiService().loadItems({
    ids: [id as string],
  } as TransactionFilter);

  toastifyResponse(response, "failed");

  return response;
};

export const loadTransactionsList = async (transactionsIds: string[]) => {
  const response = await new TransactionsApiService().loadItems({
    ids: transactionsIds,
  } as TransactionFilter);

  toastifyResponse(response, "failed");

  return response;
};

export const saveTransaction = async (
  transaction: TransactionSTO,
  onlyFailedMessage?: boolean
): Promise<SaveResponse> => {
  const response = await new TransactionsApiService().saveItems([transaction]);

  onlyFailedMessage
    ? toastifyResponse(response, "failed")
    : toastifyResponse(response, "all", "default_success_message", "default_cancellation_message", "error_saving_data");

  return response;
};

export const saveTransactions = async (
  transactions: TransactionSTO[],
  onlyFailedMessage?: boolean
): Promise<SaveResponse> => {
  const response = await new TransactionsApiService().saveItems(transactions);

  onlyFailedMessage
    ? toastifyResponse(response, "failed")
    : toastifyResponse(response, "all", "default_success_message", "default_cancellation_message", "error_saving_data");

  return response;
};
